/** The three project-drawer sections: milestones, dependencies, and who is on it.
 *
 * Milestones are shown even when undated. A milestone nobody has put a date on is a
 * commitment nobody has pinned down, which is worth seeing rather than hiding until
 * someone remembers.
 */

import { useState } from 'react'
import { AlertTriangle, Check, Link2, Plus, Trash2, X } from 'lucide-react'

import { SearchSelect } from '@/components/SearchSelect'
import { Button, DateInput, ErrorState, Field, Pill, SectionHeader, inputClass } from '@/components/ui'
import { formatDate, ragTone, statusLabel, type Tone } from '@/lib/domain'
import { useProjects } from '@/features/projects/useProjects'
import {
  useAddDependency,
  useAddMilestone,
  useAssign,
  useAssignments,
  useDeleteMilestone,
  useDependencies,
  useMilestones,
  useRemoveDependency,
  useUnassign,
  useUpdateMilestone,
  type Assignment,
  type Milestone,
} from '@/features/delivery/useDelivery'
import { usePeople } from '@/features/people/usePeople'

const STATE_TONE: Record<Milestone['state'], Tone> = {
  complete: 'green',
  overdue: 'red',
  due_soon: 'amber',
  upcoming: 'info',
  undated: 'neutral',
}

const STATE_LABEL: Record<Milestone['state'], string> = {
  complete: 'Done',
  overdue: 'Overdue',
  due_soon: 'Due soon',
  upcoming: 'Upcoming',
  undated: 'No date',
}

const ROLE_LABEL: Record<Assignment['role'], string> = {
  lead: 'Lead',
  contributor: 'Contributor',
  stakeholder: 'Stakeholder',
}

// --- milestones ------------------------------------------------------------

/** How much work is linked to a milestone and how much of it is done.
 *
 * Progress only. When every linked item is finished it says so, and leaves the tick to a
 * person: work being done and the milestone being met are not the same thing (Brian,
 * 2026-09-29).
 */
function MilestoneProgressLine({ milestone }: { milestone: Milestone }) {
  const progress = milestone.progress
  if (!progress) return null
  if (progress.itemCount === 0) {
    return (
      <p className="ml-8 mt-1 text-[11px] text-fg-muted">
        No work linked — link work items to it from the Workbench.
      </p>
    )
  }
  return (
    <div className="ml-8 mt-1.5">
      <div className="flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded-pill bg-canvas">
          <div
            className="h-full rounded-pill"
            style={{
              width: `${progress.percentComplete}%`,
              backgroundColor: progress.allFinished ? 'var(--status-green)' : 'var(--status-info)',
            }}
          />
        </div>
        <span className="shrink-0 text-[11px] text-fg-muted">
          {progress.itemCount} {progress.itemCount === 1 ? 'item' : 'items'} ·{' '}
          {progress.donePoints} of {progress.committedPoints} points · {progress.percentComplete}%
        </span>
      </div>
      {progress.allFinished && !milestone.isComplete ? (
        <p className="mt-1 text-[11px]" style={{ color: 'var(--status-green)' }}>
          All linked work is done. Tick the milestone once it has actually been met.
        </p>
      ) : null}
    </div>
  )
}

export function Milestones({ projectId }: { projectId: string }) {
  const { data, error } = useMilestones(projectId)
  const add = useAddMilestone(projectId)
  const update = useUpdateMilestone(projectId)
  const remove = useDeleteMilestone(projectId)
  const [title, setTitle] = useState('')
  const [due, setDue] = useState('')
  /** Which row is being edited in place, and the draft values while it is. */
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftTitle, setDraftTitle] = useState('')
  const [draftDue, setDraftDue] = useState('')

  const milestones = data ?? []

  return (
    <>
      <SectionHeader>Milestones</SectionHeader>
      {error ? <ErrorState error={error} /> : null}

      {milestones.length === 0 ? (
        <p className="mb-3 text-sm text-fg-muted">
          Dated commitments, separate from the work items that get you there.
        </p>
      ) : (
        <ul className="card mb-3 divide-y divide-border">
          {milestones.map((milestone) => (
            <li key={milestone.id} className="px-3 py-2">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  aria-label={milestone.isComplete ? 'Mark not done' : 'Mark done'}
                  onClick={() =>
                    update.mutate({
                      id: milestone.id,
                      is_complete: !milestone.isComplete,
                      row_version: milestone.rowVersion,
                    })
                  }
                  className="flex h-5 w-5 shrink-0 items-center justify-center rounded border border-border"
                  style={
                    milestone.isComplete
                      ? { backgroundColor: 'var(--status-green)', borderColor: 'var(--status-green)' }
                      : undefined
                  }
                >
                  {milestone.isComplete ? <Check size={13} color="white" aria-hidden /> : null}
                </button>
                {/* Editable in place. The API could change a title and a date from the
                    start; nothing exposed it, so the only way to correct a date was to
                    delete the milestone and retype it. */}
                {editingId === milestone.id ? (
                  <>
                    <input
                      className={`${inputClass} min-w-0 flex-1`}
                      value={draftTitle}
                      onChange={(event) => setDraftTitle(event.target.value)}
                      aria-label="Milestone title"
                    />
                    <DateInput
                      className="w-40 shrink-0"
                      value={draftDue}
                      onChange={setDraftDue}
                      aria-label="Due date"
                    />
                    <Button
                      variant="primary"
                      disabled={!draftTitle.trim() || update.isPending}
                      onClick={() =>
                        update.mutate(
                          {
                            id: milestone.id,
                            title: draftTitle.trim(),
                            // An empty field means "take the date off", which needs saying
                            // explicitly — a missing field is indistinguishable from
                            // "unchanged" in a partial update.
                            ...(draftDue ? { due_date: draftDue } : { clear_due_date: true }),
                            row_version: milestone.rowVersion,
                          },
                          { onSuccess: () => setEditingId(null) },
                        )
                      }
                    >
                      Save
                    </Button>
                    <Button onClick={() => setEditingId(null)}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      className="min-w-0 flex-1 truncate text-left text-sm text-fg hover:underline"
                      style={milestone.isComplete ? { textDecoration: 'line-through' } : undefined}
                      title={`${milestone.title} — press to edit`}
                      onClick={() => {
                        setEditingId(milestone.id)
                        setDraftTitle(milestone.title)
                        setDraftDue(milestone.dueDate ?? '')
                      }}
                    >
                      {milestone.title}
                    </button>
                    <Pill tone={STATE_TONE[milestone.state]}>{STATE_LABEL[milestone.state]}</Pill>
                    <button
                      type="button"
                      className="w-24 shrink-0 text-right text-xs text-fg-muted hover:text-fg hover:underline"
                      title="Press to change the date"
                      onClick={() => {
                        setEditingId(milestone.id)
                        setDraftTitle(milestone.title)
                        setDraftDue(milestone.dueDate ?? '')
                      }}
                    >
                      {formatDate(milestone.dueDate)}
                    </button>
                    <button
                      type="button"
                      aria-label="Delete milestone"
                      className="text-fg-muted hover:text-fg"
                      onClick={() =>
                        remove.mutate({ id: milestone.id, reason: 'Removed from the drawer' })
                      }
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </>
                )}
              </div>
              {milestone.progress ? <MilestoneProgressLine milestone={milestone} /> : null}
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[200px] flex-1">
          <Field label="New milestone">
            <input
              className={inputClass}
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Hurricane DR cutover"
            />
          </Field>
        </div>
        <Field label="Due">
          <DateInput value={due} onChange={setDue} />
        </Field>
        <Button
          disabled={!title.trim() || add.isPending}
          onClick={() =>
            add.mutate(
              { title: title.trim(), due_date: due || null },
              {
                onSuccess: () => {
                  setTitle('')
                  setDue('')
                },
              },
            )
          }
        >
          <Plus size={14} aria-hidden />
          Add
        </Button>
      </div>
      {add.isError ? <div className="mt-2"><ErrorState error={add.error} /></div> : null}
    </>
  )
}

// --- dependencies ----------------------------------------------------------

export function Dependencies({ projectId }: { projectId: string }) {
  const { data } = useDependencies(projectId)
  const { data: projects } = useProjects({})
  const add = useAddDependency(projectId)
  const remove = useRemoveDependency(projectId)
  const [target, setTarget] = useState('')

  const dependsOn = data?.dependsOn ?? []
  const blocks = data?.blocks ?? []
  const candidates = (projects?.projects ?? []).filter(
    (project) => project.id !== projectId && !dependsOn.some((d) => d.projectId === project.id),
  )

  return (
    <>
      <SectionHeader>Dependencies</SectionHeader>

      {dependsOn.length > 0 ? (
        <ul className="card mb-3 divide-y divide-border">
          {dependsOn.map((link) => (
            <li key={link.id} className="flex items-center gap-3 px-3 py-2">
              <Link2 size={14} className="shrink-0 text-fg-muted" aria-hidden />
              <span className="min-w-0 flex-1 truncate text-sm text-fg">
                Waiting on <span className="font-medium">{link.name}</span>
              </span>
              {link.ragStatus && link.ragStatus !== 'none' ? (
                <Pill tone={ragTone(link.ragStatus)}>{link.ragStatus}</Pill>
              ) : null}
              <span className="text-xs text-fg-muted">{statusLabel(link.status)}</span>
              <button
                type="button"
                aria-label="Remove dependency"
                className="text-fg-muted hover:text-fg"
                onClick={() => remove.mutate({ id: link.id, reason: 'Removed from the drawer' })}
              >
                <X size={14} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mb-3 text-sm text-fg-muted">Nothing is blocking this project.</p>
      )}

      {blocks.length > 0 ? (
        <div className="mb-3">
          <p className="micro-label mb-1 text-fg-muted">Blocking</p>
          <ul className="card divide-y divide-border">
            {blocks.map((link) => (
              <li key={link.id} className="px-3 py-2 text-sm text-fg">
                {link.name} <span className="text-xs text-fg-muted">is waiting on this</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[220px] flex-1">
          <Field label="This project waits on">
            <SearchSelect
              value={target}
              onChange={setTarget}
              emptyLabel="Choose a project…"
              options={candidates.map((project) => ({
                value: project.id,
                label: project.name,
                hint: project.teamShortLabel,
              }))}
            />
          </Field>
        </div>
        <Button
          disabled={!target || add.isPending}
          onClick={() =>
            add.mutate({ depends_on_id: target }, { onSuccess: () => setTarget('') })
          }
        >
          <Plus size={14} aria-hidden />
          Link
        </Button>
      </div>
      {add.isError ? <div className="mt-2"><ErrorState error={add.error} /></div> : null}
    </>
  )
}

// --- assignments -----------------------------------------------------------

export function Assignments({ projectId }: { projectId: string }) {
  const { data } = useAssignments(projectId)
  // `/people`, not `/admin/users`: the admin list is empty for anyone who is not an
  // Administrator, which left this picker blank for the people who staff projects.
  const { data: people } = usePeople()
  const assign = useAssign(projectId)
  const unassign = useUnassign(projectId)
  const [userId, setUserId] = useState('')
  const [role, setRole] = useState<Assignment['role']>('contributor')

  const assignments = data ?? []
  const assigned = new Set(assignments.map((assignment) => assignment.userId))
  const candidates = (people ?? []).filter((person) => !assigned.has(person.id))

  return (
    <>
      <SectionHeader>Team</SectionHeader>

      {assignments.length === 0 ? (
        <p className="mb-3 text-sm text-fg-muted">
          Nobody assigned yet. The owner answers for the project; this is everyone on it.
        </p>
      ) : (
        <ul className="card mb-3 divide-y divide-border">
          {assignments.map((assignment) => (
            <li key={assignment.id} className="flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-fg">{assignment.displayName}</div>
                <div className="truncate text-xs text-fg-muted">{assignment.email}</div>
              </div>
              <Pill tone={assignment.role === 'lead' ? 'info' : 'neutral'}>
                {ROLE_LABEL[assignment.role]}
              </Pill>
              <button
                type="button"
                aria-label="Remove from project"
                className="text-fg-muted hover:text-fg"
                onClick={() =>
                  unassign.mutate({ id: assignment.id, reason: 'Removed from the drawer' })
                }
              >
                <X size={14} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[200px] flex-1">
          <Field label="Add someone">
            <SearchSelect
              value={userId}
              onChange={setUserId}
              emptyLabel="Choose…"
              options={candidates.map((person) => ({
                value: person.id,
                label: person.isExternal ? `${person.displayName} (external)` : person.displayName,
                hint: person.email,
              }))}
            />
          </Field>
        </div>
        <Field label="As">
          <select
            className={inputClass}
            value={role}
            onChange={(event) => setRole(event.target.value as Assignment['role'])}
          >
            <option value="lead">Lead</option>
            <option value="contributor">Contributor</option>
            <option value="stakeholder">Stakeholder</option>
          </select>
        </Field>
        <Button
          disabled={!userId || assign.isPending}
          onClick={() =>
            assign.mutate({ user_id: userId, role }, { onSuccess: () => setUserId('') })
          }
        >
          <Plus size={14} aria-hidden />
          Add
        </Button>
      </div>
      {assign.isError ? <div className="mt-2"><ErrorState error={assign.error} /></div> : null}
    </>
  )
}

// --- inversions ------------------------------------------------------------

export function InversionBanner({
  inversions,
}: {
  inversions: { projectName: string; dependsOnName: string; projectRank: number; dependsOnRank: number | null }[]
}) {
  if (inversions.length === 0) return null

  return (
    <div
      className="card mb-4 p-4"
      role="alert"
      style={{ borderLeft: '3px solid var(--status-amber)' }}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle size={16} style={{ color: 'var(--status-amber)' }} aria-hidden />
        <span className="font-heading text-lg text-fg">
          {inversions.length === 1
            ? 'One project is ranked above something it depends on'
            : `${inversions.length} projects are ranked above something they depend on`}
        </span>
      </div>
      <p className="mt-1 text-sm text-fg-muted">
        The order below schedules work before the thing it waits on. Neither project shows this
        on its own.
      </p>
      <ul className="mt-3 space-y-1 text-sm">
        {inversions.map((inversion) => (
          <li key={`${inversion.projectName}-${inversion.dependsOnName}`} className="text-fg">
            <span className="font-medium">{inversion.projectName}</span>{' '}
            <span className="text-fg-muted">(rank {inversion.projectRank})</span> waits on{' '}
            <span className="font-medium">{inversion.dependsOnName}</span>{' '}
            <span className="text-fg-muted">
              ({inversion.dependsOnRank === null ? 'unscored' : `rank ${inversion.dependsOnRank}`})
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
